import dayjs from 'dayjs';

import {
  Box,
  Column,
  EllipsisWithTooltip,
  Icons,
  Skeleton,
  SkeletonTable,
  Stack,
  StatusChipV2,
  Table,
  TableProps,
  tokens,
  Tooltip,
  Typography,
} from '@cast/design-system';
import { StorageAction } from '@cast/types-stop';

import { DATE_TIME_NO_SEC } from '@cast/constants';
import { getActionOperationChanges } from 'features/cluster/storage-optimization/pv-events/utils/getActionOperationChanges';
import { computeDeltaPercentage, getActionOperationTitles } from '../_util';

type CellProps = {
  action: StorageAction;
};

const ValueChangedCell = ({ action }: CellProps) => {
  const changes = getActionOperationChanges(action);

  return (
    <Stack gap={6} flexWrap="wrap" my={changes.length ? 8 : 0}>
      {changes.map((change) => (
        <Stack key={change.label} direction="row" alignItems="center" gap={6}>
          <Typography variant="P12B">{change.label}:</Typography>
          <Stack gap={2} direction="row" alignItems="center">
            {change.before !== 0 && (
              <>
                <Typography
                  variant="P12R"
                  color={tokens.theme.fg.structural.secondary}
                >
                  {change.before}
                </Typography>
                <Icons.ArrowRight
                  css={({ palette }) => ({ color: palette.grey[600] })}
                />
              </>
            )}
            <Typography variant={change.before === 0 ? 'P12R' : 'P12M'}>
              {change.after}
            </Typography>
            {change.unit && (
              <Typography
                variant="P12R"
                color={tokens.theme.fg.structural.secondary}
              >
                {' '}
                {change.unit}
              </Typography>
            )}
          </Stack>
        </Stack>
      ))}
    </Stack>
  );
};

const DeltaCell = ({ action }: CellProps) => {
  const changes = getActionOperationChanges(action);

  return (
    <Stack gap={6} flexWrap="wrap" my={changes.length ? 8 : 0}>
      {changes.map((change) => {
        if (change.before === 0) {
          return <Typography key={change.label}>-</Typography>;
        }

        const changePercentage = computeDeltaPercentage(change);
        const isPositive = changePercentage > 0;
        const isNegative = changePercentage < 0;

        return (
          <StatusChipV2
            key={change.label}
            size="small"
            color={isPositive ? 'success' : isNegative ? 'error' : 'neutral'}
            label={`${Math.abs(changePercentage)} %`}
            slots={{
              icon: isPositive ? (
                <Icons.ArrowUpRight />
              ) : isNegative ? (
                <Icons.ArrowDownRight />
              ) : undefined,
            }}
          />
        );
      })}
    </Stack>
  );
};

const ChangeTypeCell = ({ action }: CellProps) => {
  const titles = getActionOperationTitles(action);

  return <EllipsisWithTooltip>{titles.join(', ')}</EllipsisWithTooltip>;
};

const TimestampCell = ({ action }: CellProps) => {
  return (
    <Stack direction="row" alignItems="center" gap={4}>
      <Typography variant="P12R">
        {action.ts ? dayjs(action.ts).format(DATE_TIME_NO_SEC) : '-'}
      </Typography>
      {action.status === 'failed' && (
        <Tooltip
          title={
            <Typography variant="P12R">
              {action.error ?? 'Action failed with unknown reason'}
            </Typography>
          }
        >
          <Icons.Warning
            weight="fill"
            size={14}
            data-testid="failed-action-marker"
            css={({ palette }) => ({ color: palette.red[400] })}
          />
        </Tooltip>
      )}
    </Stack>
  );
};

type ResizeEventsTableProps = {
  storageActions: StorageAction[];
  isFetchingNextPage?: boolean;
  hideFirstAndLastColumnPaddings?: boolean;
} & Omit<TableProps<StorageAction>, 'data' | 'rowKey'>;

export const ResizeEventsTable = ({
  storageActions,
  isFetchingNextPage,
  hideFirstAndLastColumnPaddings = false,
  ...rest
}: ResizeEventsTableProps) => {
  return (
    <Table<StorageAction>
      noShadow
      {...rest}
      data={storageActions}
      rowKey={(row) => `${row.operationId}-${row.ts}`}
      getRowTestId={(row) => `action-row-${row.operationId}`}
      testId="resize-events-table"
      loadingRowSkeleton={
        <Skeleton
          height={25}
          sx={hideFirstAndLastColumnPaddings ? {} : { mx: 16 }}
        />
      }
      footer={
        isFetchingNextPage ? (
          <Box sx={hideFirstAndLastColumnPaddings ? {} : { px: 16 }}>
            <SkeletonTable rowCount={5} size="small" />
          </Box>
        ) : undefined
      }
    >
      <Column<StorageAction>
        id="when"
        header="Timestamp"
        cellWrapperSx={hideFirstAndLastColumnPaddings ? { paddingLeft: 0 } : {}}
        renderCell={(row) => <TimestampCell action={row} />}
        minWidth={120}
      />
      <Column<StorageAction>
        id="changeType"
        header="Change type"
        renderCell={(row) => <ChangeTypeCell action={row} />}
        minWidth={160}
      />
      <Column<StorageAction>
        id="valueChange"
        header="Value change"
        renderCell={(row) => <ValueChangedCell action={row} />}
        minWidth={180}
      />
      <Column<StorageAction>
        id="delta"
        header="Delta"
        renderCell={(row) => <DeltaCell action={row} />}
        minWidth={70}
        cellWrapperSx={
          hideFirstAndLastColumnPaddings ? { paddingRight: 0 } : {}
        }
      />
    </Table>
  );
};
